/**
 * Utility functions for handling rider benefits in UHIC/Oxford migrated groups
 * 
 * For migrated UHIC/Oxford groups, plans come from the /cirrus/med-plan-details API with different
 * rider combinations. Each rider combination has a unique planKey but shares the same codeDisplay.
 */

export const groupPlansByRiders = (medicalPlans) => {
    
    if (!medicalPlans || !Array.isArray(medicalPlans)) {
        return {
            nonRiderPlans: [],
            riderMapping: {}
        };
    }

    const nonRiderPlans = [];
    const riderMapping = {};

    medicalPlans.forEach(plan => {
        const riders = plan.riders || 'N/A';
        const codeDisplay = plan.codeDisplay;
        const isNonRiderPlan = riders === 'N/A';

        if (isNonRiderPlan) {
            nonRiderPlans.push(plan);
        } else {
            if (!riderMapping[codeDisplay]) {
                riderMapping[codeDisplay] = [];
            }
            riderMapping[codeDisplay].push({
                riders: riders,
                planKey: plan.planKey,
                code: plan.code,
                ...plan
            });
        }
    });

    return {
        nonRiderPlans,
        riderMapping
    };
};

/**
 * Extracts unique rider names from a pipe-separated string
 * Example: "Rider A | Domestic Partner" => ["Rider A", "Domestic Partner"]
 * 
 * @param {String} ridersString - Pipe-separated rider names
 * @returns {Array} Array of unique rider names
 */
export const parseRiderNames = (ridersString) => {
    if (!ridersString || ridersString === 'N/A') {
        return [];
    }

    return ridersString
        .split('|')
        .map(rider => rider.trim())
        .filter(rider => rider !== '' && rider !== 'N/A');
};

/**
 * Gets available rider options for selected plans based on riderMapping
 * 
 * @param {Array} selectedPlans - Array of selected medical plans
 * @param {Object} riderMapping - Mapping of codeDisplay to rider variations
 * @returns {Array} Array of objects with planCode and available riders
 *   Example: [
 *     { planCode: "CT G FRDM NG 30/60/1500/80 PPO 26", riders: ["Rider A", "Rider B"] }
 *   ]
 */
export const getAvailableRidersForPlans = (selectedPlans, riderMapping) => {
    if (!selectedPlans || !riderMapping) {
        return [];
    }

    const availableRiders = [];

    selectedPlans.forEach(plan => {
        const codeDisplay = plan.codeDisplay;
        const riderVariations = riderMapping[codeDisplay];

        if (riderVariations && riderVariations.length > 0) {
            const ridersSet = new Set();
            
            riderVariations.forEach(variation => {
                const riders = parseRiderNames(variation.riders);
                riders.forEach(rider => ridersSet.add(rider));
            });

            if (ridersSet.size > 0) {
                availableRiders.push({
                    planCode: codeDisplay,
                    codeDisplay: codeDisplay,
                    riders: Array.from(ridersSet).sort()
                });
            }
        }
    });

    return availableRiders;
};

/**
 * Finds the planKey for a specific rider combination
 * 
 * @param {String} codeDisplay - Plan code display name
 * @param {Array} selectedRiders - Array of selected rider names
 * @param {Object} riderMapping - Mapping of codeDisplay to rider variations
 * @returns {Object} Plan object with matching rider combination, or null if not found
 */
export const findPlanKeyForRiders = (codeDisplay, selectedRiders, riderMapping) => {
    const riderVariations = riderMapping[codeDisplay];
    
    if (!riderVariations || !selectedRiders || selectedRiders.length === 0) {
        return null;
    }

    const selectedRidersSet = new Set(selectedRiders.map(r => r.trim()));
    const matchingPlan = riderVariations.find(variation => {
        const planRiders = parseRiderNames(variation.riders);
        const planRidersSet = new Set(planRiders);
        return selectedRiders.every(rider => planRidersSet.has(rider)) &&
               planRidersSet.size === selectedRidersSet.size;
    });

    return matchingPlan || null;
};

/**
 * Finds the plan that matches the exact combination of selected riders
 * 
 * @param {String} codeDisplay - Plan code display name
 * @param {Array} selectedRiders - Array of selected rider names (e.g., ["Domestic Partner", "Dependent Age 29"])
 * @param {Object} riderMapping - Mapping of codeDisplay to rider variations
 * @returns {Array} Array containing the single plan object that matches the exact rider combination
 */
export const findPlansForSelectedRiders = (codeDisplay, selectedRiders, riderMapping) => {
    const riderVariations = riderMapping[codeDisplay];
    
    if (!riderVariations || !selectedRiders || selectedRiders.length === 0) {
        return [];
    }

    const matchingPlan = findPlanKeyForRiders(codeDisplay, selectedRiders, riderMapping);
    
    return matchingPlan ? [matchingPlan] : [];
};

/**
 * Checks if a plan has optional riders available
 * 
 * @param {String} codeDisplay - Plan code display name
 * @param {Object} riderMapping - Mapping of codeDisplay to rider variations
 * @returns {Boolean} True if plan has optional riders available
 */
export const hasOptionalRiders = (codeDisplay, riderMapping) => {
    return riderMapping && 
           riderMapping[codeDisplay] && 
           riderMapping[codeDisplay].length > 0;
};

/**
 * Creates a display-friendly list of riders for UI
 * 
 * @param {Array} riderVariations - Array of plan variations with riders
 * @returns {Array} Array of rider combination strings
 *   Example: ["Rider A", "Rider B", "Rider A | Rider B"]
 */
export const getRiderCombinationsForDisplay = (riderVariations) => {
    if (!riderVariations || riderVariations.length === 0) {
        return [];
    }

    const combinations = new Set();
    
    riderVariations.forEach(variation => {
        const riders = parseRiderNames(variation.riders);
        if (riders.length > 0) {
            combinations.add(riders.join(' | '));
        }
    });

    return Array.from(combinations).sort();
};
